<template>
  <AppDialogContent
    v-model="dialog"
    :errors="formError"
    :loading="loading || formSubmitted"
    :persistent="formSubmitted"
    data-test="competingLevelForm"
    @submit-form="submit"
  >
    <template #header>
      {{ texts.title }}
    </template>

    <p>
      {{ texts.hint }}
    </p>
    <v-form :disabled="loading || formSubmitted">
      <v-progress-linear v-if="loading" indeterminate class="mb-5" />

      <AppSelectPerson
        v-if="v$.form.personId"
        v-model="form.personId"
        :input-props="{
          label: $t(
            `common:competingDifficultyLevels.${formTranslationType}.modal.personId.label`,
            'Person name'
          ),
          clearable: false,
          errorMessages: fieldErrorMessages('personId'),
          disabled: fieldDisabled('personId')
        }"
        show-details
        data-test="personId"
        @blur="() => v$.form.personId.$touch()"
      />

      <AppSelectHorse
        v-if="v$.form.horseId"
        v-model="form.horseId"
        :input-props="{
          label: $t(
            `common:competingDifficultyLevels.${formTranslationType}.modal.horseId.label`,
            'Horse name'
          ),
          clearable: false,
          errorMessages: fieldErrorMessages('horseId'),
          disabled: fieldDisabled('horseId')
        }"
        data-test="horseId"
        @blur="() => v$.form.horseId.$touch()"
      />

      <AppSelectCompetingDifficultyLevelDefinition
        v-if="v$.form.competingDifficultyLevelDefinitionId"
        v-model="form.competingDifficultyLevelDefinitionId"
        :filters="{
          groupId: form.competingDifficultyLevelGroupId
        }"
        data-test="competingDifficultyLevelDefinitionId"
        :input-props="{
          label: $t(
            `common:competingDifficultyLevels.${formTranslationType}.modal.competingDifficultyLevelDefinitionId.label`,
            'Level'
          ),
          errorMessages: fieldErrorMessages(
            'competingDifficultyLevelDefinitionId'
          ),
          disabled: fieldDisabled('competingDifficultyLevelDefinitionId')
        }"
        @blur="() => v$.form.competingDifficultyLevelDefinitionId.$touch()"
      />

      <AppDatePicker
        v-if="v$.form.validFrom"
        v-model="form.validFrom"
        :label="
          $t(
            `common:competingDifficultyLevels.${formTranslationType}.modal.validFrom.label`,
            'Level period start date'
          )
        "
        :hint="
          $t(
            `common:competingDifficultyLevels.${formTranslationType}.modal.validFrom.hint`,
            'Select from which date the entity (athlete, horse or combination) has / has had the selected level. The date cannot overlap with any other levels for the same entity'
          )
        "
        persistent-hint
        data-test="validFrom"
        :error-messages="fieldErrorMessages('validFrom')"
        :disabled="fieldDisabled('validFrom')"
        @blur="() => v$.form.validFrom.$touch()"
      />

      <AppDatePicker
        v-if="v$.form.validUntil"
        v-model="formValidUntil"
        :label="
          $t(
            `common:competingDifficultyLevels.${formTranslationType}.modal.validUntil.label`,
            'Level period end date'
          )
        "
        :hint="
          $t(
            `common:competingDifficultyLevels.${formTranslationType}.modal.validUntil.hint`,
            'Select the end date period for the level. The date cannot overlap with any other levels for the same entity.'
          )
        "
        :clearable="form.validUntil != '2199-01-01'"
        data-test="validUntil"
        persistent-hint
        :error-messages="fieldErrorMessages('validUntil')"
        :disabled="fieldDisabled('validUntil')"
        @blur="() => v$.form.validUntil.$touch()"
      />

      <template v-if="usesPositivePointsBalance">
        <h3 class="mb-2">
          {{
            $t(
              `common:competingDifficultyLevels.${formTranslationType}.modal.positivePoints.subheading`,
              "Positive points"
            )
          }}
        </h3>
        <div class="mb-2">
          {{
            $t(
              `common:competingDifficultyLevels.${formTranslationType}.modal.calculatedPositivePoints.label`,
              "Calculated positive points"
            )
          }}
          {{ form.calculatedPositivePoints }} ({{
            form.positivePointsResultsCount
          }})
        </div>
        <AppInputNumeric
          v-if="v$.form.manualPositivePoints"
          v-model="form.manualPositivePoints"
          :label="
            $t(
              `common:competingDifficultyLevels.${formTranslationType}.modal.basePositivePoints.label`,
              'Base points'
            )
          "
          :input-props="{
            hint: $t(
              `common:competingDifficultyLevels.${formTranslationType}.modal.basePositivePoints.hint`,
              'Add the points that will be added to the positive points collected via automated calculation.'
            ),
            errorMessages: fieldErrorMessages('manualPositivePoints'),
            disabled: fieldDisabled('manualPositivePoints'),
            persistentHint: true
          }"
          @blur="() => v$.form.manualPositivePoints.$touch()"
        />
      </template>

      <template v-if="usesNegativePointsBalance">
        <h3 class="mb-2">
          {{
            $t(
              `common:competingDifficultyLevels.${formTranslationType}.modal.negativePoints.subheading`,
              "Negative points"
            )
          }}
        </h3>
        <div class="mb-2">
          {{
            $t(
              `common:competingDifficultyLevels.${formTranslationType}.modal.calculatedNegativePoints.label`,
              "Calculated negative points"
            )
          }}
          -{{ form.calculatedNegativePoints }} ({{
            form.negativePointsResultsCount
          }})
        </div>
        <AppInputNumeric
          v-if="v$.form.manualNegativePoints"
          v-model="form.manualNegativePoints"
          :label="
            $t(
              `common:competingDifficultyLevels.${formTranslationType}.modal.baseNegativePoints.label`,
              'Base negative points'
            )
          "
          :input-props="{
            hint: $t(
              `common:competingDifficultyLevels.${formTranslationType}.modal.baseNegativePoints.hint`,
              'Add the points that will be added to the negative points collected via automated calculation.'
            ),
            errorMessages: fieldErrorMessages('manualNegativePoints'),
            disabled: fieldDisabled('manualNegativePoints'),
            persistentHint: true
          }"
          @blur="() => v$.form.manualNegativePoints.$touch()"
        />
      </template>

      <template v-if="usesPositivePointsBalance || usesNegativePointsBalance">
        <div class="my-5" style="font-weight: bold; font-size: 15px">
          {{
            $t(
              "common:competingDifficultyLevels.editLevel.modal.pointsBalance.label",
              "Points balance:"
            )
          }}
          {{ pointsBalance }}
        </div>
      </template>
    </v-form>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed } from "vue";
//import { useTranslation } from "i18next-vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useQuery } from "@vue/apollo-composable";
import AppDialogContent from "@elements/AppDialogContent.vue";
import { useTranslation } from "i18next-vue";
import { useFormValidation } from "@/composables/form/form_validation";
import { useSaveMeta } from "@/composables/form/save_meta";

import AppSelectPerson from "@AppSelects/AppSelectPerson.vue";
import AppSelectHorse from "@AppSelects/AppSelectHorse.vue";
import AppSelectCompetingDifficultyLevelDefinition from "@AppSelects/AppSelectCompetingDifficultyLevelDefinition.vue";
import AppDatePicker from "@elements/AppDatePicker.vue";
import AppInputNumeric from "../AppInputs/AppInputNumeric.vue";
import { injectSnackbarError } from "@composables/injections";
import { setDefaultValuesFromFormField } from "@/utils/inputs/set_default_values";

defineOptions({
  name: "CompetingDifficultyLevelForm"
});

const emit = defineEmits(["refetch"]);

const { t } = useTranslation();
const { snackbarServerError } = injectSnackbarError();

const dialog = ref(false);
const form = ref({
  id: null,
  competingDifficultyLevelDefinitionId: null,
  personId: null,
  horseId: null,
  competingDifficultyLevelGroupId: null,
  validFrom: null,
  validUntil: null
});

const formError = ref(null);
const { saveMetaSetOriginalForm } = useSaveMeta(form, { usageInModal: true });

const formValidUntil = computed({
  get() {
    return form.value.validUntil;
  },
  set(v) {
    if (v) form.value.validUntil = v;
    else form.value.validUntil = "2199-01-01";
  }
});

const formSubmitted = ref(false);

const formTranslationType = computed(() => {
  if (form.value.id) return "editLevel";
  return "addLevel";
});

const texts = computed(() => {
  if (form.value.id) {
    return {
      title: t(
        "common:competingDifficultyLevels.editLevel.modal.heading",
        "Edit level"
      ),
      hint: t(
        "common:competingDifficultyLevels.editLevel.modal.hint",
        "You are going to edit selected level. You can change start date and end date. Please note that there can be one level assigned for the entity for one period of time."
      )
    };
  }

  return {
    title: t(
      "common:competingDifficultyLevels.addLevel.modal.heading",
      "Add level"
    ),
    hint: t(
      "common:competingDifficultyLevels.addLevel.modal.hint",
      "You are going to add a new level. Please note that there can be one level assigned for the athlete, horse or combination for one period of time."
    )
  };
});

const initCompetingDifficultyLevelForm = ref({
  vuelidateValidations: {},
  readOnlyFields: []
});

const { onResult, loading } = useQuery(
  gql`
    query initCompetingDifficultyLevelForm(
      $id: ID
      $personId: ID
      $horseId: ID
      $competingDifficultyLevelGroupId: ID
    ) {
      initCompetingDifficultyLevelForm(
        id: $id
        personId: $personId
        horseId: $horseId
        competingDifficultyLevelGroupId: $competingDifficultyLevelGroupId
      ) {
        vuelidateValidations
        readOnlyFields
        competingDifficultyLevel {
          id
          personId
          horseId
          competingDifficultyLevelGroupId
          competingDifficultyLevelDefinitionId
          competingDifficultyLevelDefinition @client {
            id
            usePositivePointsBalance
            useNegativePointsBalance
          }
          validFrom
          validUntil

          calculatedPositivePoints
          positivePointsResultsCount
          manualPositivePoints

          calculatedNegativePoints
          negativePointsResultsCount
          manualNegativePoints
        }
      }
    }
  `,
  () => {
    return {
      id: form.value.id,
      personId: form.value.personId,
      horseId: form.value.horseId,
      competingDifficultyLevelGroupId:
        form.value.competingDifficultyLevelGroupId
    };
  },
  () => ({
    enabled: dialog.value
  })
);

const {
  v$,
  fieldDisabled,
  fieldErrorMessages,
  formValidate,
  formErrorMessages
} = useFormValidation(initCompetingDifficultyLevelForm, form);

onResult(({ data, loading }) => {
  if (loading) return;
  initCompetingDifficultyLevelForm.value =
    data.initCompetingDifficultyLevelForm;
  const defaultForm = Object.entries(
    data.initCompetingDifficultyLevelForm.competingDifficultyLevel
  ).reduce((arr, x) => {
    if (["competingDifficultyLevelDefinition", "__typename"].includes(x[0]))
      return arr;
    arr.push({
      name: x[0],
      defaultValue: x[1]
    });
    return arr;
  }, []);

  const formy = setDefaultValuesFromFormField(form.value, defaultForm, {
    validationObject: v$.value.form,
    forceReturn: true
  });
  form.value = formy;

  if (!form.value.id) {
    saveMetaSetOriginalForm(form.value);
  }
});

const usesPositivePointsBalance = computed(() => {
  return (
    initCompetingDifficultyLevelForm.value.competingDifficultyLevel
      ?.competingDifficultyLevelDefinition?.usePositivePointsBalance === true
  );
});
const usesNegativePointsBalance = computed(() => {
  return (
    initCompetingDifficultyLevelForm.value.competingDifficultyLevel
      ?.competingDifficultyLevelDefinition?.useNegativePointsBalance === true
  );
});

const pointsBalance = computed(() => {
  return (
    (form.value.calculatedPositivePoints || 0) +
    (form.value.manualPositivePoints || 0) -
    (form.value.calculatedNegativePoints || 0) -
    (form.value.manualNegativePoints || 0)
  );
});

function newCompetingLevel(item) {
  form.value = {
    id: null,
    personId: item.personId ?? null,
    horseId: item.horseId ?? null,
    competingDifficultyLevelDefinitionId: null,
    competingDifficultyLevelGroupId:
      item.competingDifficultyLevelGroupId ?? null,
    validFrom: null,
    validUntil: null
  };
  saveMetaSetOriginalForm(form.value);
  v$.value.$reset();

  dialog.value = true;
}

function editCompetingLevel(item) {
  form.value = {
    id: item.id,
    personId: item.personId ?? null,
    horseId: item.horseId ?? null,
    competingDifficultyLevelDefinitionId:
      item.competingDifficultyLevelDefinitionId,
    competingDifficultyLevelGroupId:
      item.competingDifficultyLevelDefinition.competingDifficultyLevelGroupId,
    validFrom: item.validFrom,
    validUntil: item.validUntil ?? null
  };

  saveMetaSetOriginalForm(form.value);
  v$.value.$reset();
  dialog.value = true;
}

async function submit() {
  formError.value = null;
  if (!formValidate()) {
    formError.value = formErrorMessages();
    return;
  }

  try {
    formSubmitted.value = true;
    const promise = form.value.id ? update : create;
    await promise();
    saveMetaSetOriginalForm(form.value);
    emit("refetch");
    dialog.value = false;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    formSubmitted.value = false;
  }

  function create() {
    return apolloClient.mutate({
      mutation: gql`
        mutation competingDifficultyLevelCreate(
          $competingDifficultyLevelDefinitionId: ID!
          $personId: ID
          $horseId: ID
          $validFrom: Date
          $validUntil: Date
          $manualPositivePoints: Float
          $manualNegativePoints: Float
        ) {
          competingDifficultyLevelCreate(
            input: {
              competingDifficultyLevelDefinitionId: $competingDifficultyLevelDefinitionId
              personId: $personId
              horseId: $horseId
              validFrom: $validFrom
              validUntil: $validUntil
              manualPositivePoints: $manualPositivePoints
              manualNegativePoints: $manualNegativePoints
            }
          ) {
            competingDifficultyLevel {
              id
              personId
              horseId
              validFrom
              validUntil
              pointsBalance
            }
          }
        }
      `,
      variables: form.value
    });
  }
  function update() {
    return apolloClient.mutate({
      mutation: gql`
        mutation competingDifficultyLevelUpdate(
          $id: ID!
          $validFrom: Date
          $validUntil: Date
          $manualPositivePoints: Float
          $manualNegativePoints: Float
        ) {
          competingDifficultyLevelUpdate(
            input: {
              id: $id
              validFrom: $validFrom
              validUntil: $validUntil
              manualPositivePoints: $manualPositivePoints
              manualNegativePoints: $manualNegativePoints
            }
          ) {
            competingDifficultyLevel {
              id
              horseId
              personId
              validFrom
              validUntil
              pointsBalance
            }
          }
        }
      `,
      variables: form.value
    });
  }
}

defineExpose({
  // Called by parent component
  newCompetingLevel,
  editCompetingLevel
});
</script>
